import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Tab from '@mui/material/Tab';
import AppTabs from '../../../../components/AppTabs';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import { PosicaoAtualAtivoItem } from '../../../../types/carteiraDiaria';
import { formatCodigo, formatBRL, formatPercent, formatQtd } from '../../../../utils/formatters';
import HistoricoMovimentacoesSection from '../../../carteira/components/HistoricoMovimentacoesSection';
import RentabilidadeAtivoSection from '../../../carteira/components/RentabilidadeAtivoSection';
import HistoricoAtivoSection from '../../../carteira/components/HistoricoAtivoSection';
import CronogramaAtivoSection from '../../../carteira/components/CronogramaAtivoSection';

export const NUM_COLS = 10;

const ellipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const tdSx = {
  ...ellipsis,
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  color: '#2D3748',
};

const formatRentDiaria = (ln: number | null): string => {
  if (ln == null) return '--';
  const pct = (Math.exp(ln) - 1) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(4)}%`;
};

const formatRentCorrigida = (r: number | null): string => {
  if (r == null) return '--';
  const pct = r * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(4)}%`;
};

const rentColor = (ln: number | null): string => {
  if (ln == null) return '#8A98A8';
  return ln >= 0 ? '#29A152' : '#E51739';
};

const getNome = (ativo: PosicaoAtualAtivoItem) => ativo.nome_canonico || ativo.nome || '—';

const getClasse = (ativo: PosicaoAtualAtivoItem): string =>
  ativo.classe_fidc_display || ativo.tit_tab || '—';

const getSubclasse = (ativo: PosicaoAtualAtivoItem): string =>
  ativo.subclasse || '—';

export interface AtivoTableRowProps {
  ativo: PosicaoAtualAtivoItem;
  flatIndex: number;
  fundoId: number;
  isExpanded: boolean;
  onToggle: (key: string) => void;
  onOpenCadastro: (ativo: PosicaoAtualAtivoItem) => void;
  hasDesvio?: boolean;
}

const AtivoTableRow: React.FC<AtivoTableRowProps> = ({
  ativo, flatIndex, fundoId, isExpanded, onToggle, onOpenCadastro, hasDesvio,
}) => {
  const [activeTab, setActiveTab] = useState<'mov' | 'rent' | 'hist' | 'cron'>('mov');
  const rowKey = `row_${flatIndex}`;
  const hasCodigo = !!ativo.codigo1;

  const handleToggle = () => {
    if (hasCodigo) {
      if (!isExpanded) setActiveTab('mov');
      onToggle(rowKey);
    }
  };

  const handleOpenMapeamento = (e: React.MouseEvent) => {
    e.stopPropagation();
    const busca = ativo.codigo1 || ativo.nome_canonico || ativo.nome;
    const ativoId = ativo.ativo_master_id;
    const params = new URLSearchParams({ busca: busca ?? '' });
    if (ativoId) params.set('ativo_id', String(ativoId));
    window.open(`/ativos/mapeamento?${params.toString()}`, '_blank');
  };

  return (
    <React.Fragment>
      <TableRow
        onClick={handleToggle}
        sx={{
          cursor: hasCodigo ? 'pointer' : 'default',
          backgroundColor: hasDesvio ? '#FFF8E7' : undefined,
          '&:nth-of-type(4n+1)': { backgroundColor: hasDesvio ? '#FFF3D0' : '#F9FAFB' },
          '&:hover': hasCodigo ? { backgroundColor: hasDesvio ? '#FFE9A0' : '#F0F7FF' } : {},
          '& > td': { borderBottom: isExpanded ? 'none' : undefined },
        }}
      >
        <TableCell sx={{ ...tdSx, p: 0.5, textAlign: 'center' }}>
          {ativo.tem_cadastro ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {ativo.condominio ? (
                <Tooltip
                  title={ativo.condominio === 'ABERTO' ? 'Condomínio Aberto' : 'Condomínio Fechado'}
                  placement="top"
                >
                  <Box sx={{
                    minWidth: 52, px: 0.75, py: 0.15, borderRadius: 0.75, cursor: 'default',
                    textAlign: 'center',
                    backgroundColor: ativo.condominio === 'ABERTO' ? '#EBF7FA' : '#F3EFF9',
                    border: `1px solid ${ativo.condominio === 'ABERTO' ? '#9DD8E8' : '#C4AEE8'}`,
                  }}>
                    <Typography sx={{
                      fontSize: '10px', fontWeight: 700, fontFamily: 'Inter', lineHeight: 1.4,
                      color: ativo.condominio === 'ABERTO' ? '#2C91AD' : '#7B5EA7',
                    }}>
                      {ativo.condominio === 'ABERTO' ? 'Aberto' : 'Fechado'}
                    </Typography>
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={`Cadastrado: ${ativo.nome_canonico}`} placement="top">
                  <Box component="span" sx={{
                    display: 'inline-block', width: 8, height: 8,
                    borderRadius: '50%', backgroundColor: '#29A152', cursor: 'default',
                  }} />
                </Tooltip>
              )}
              <Tooltip title="Ver mapeamento" placement="top">
                <IconButton size="small" onClick={handleOpenMapeamento} sx={{ p: 0.25 }}>
                  <LaunchIcon sx={{ fontSize: 12, color: '#B0BEC5', '&:hover': { color: '#2C91AD' } }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title="Sem cadastro — clique para cadastrar">
              <IconButton size="small" onClick={e => { e.stopPropagation(); onOpenCadastro(ativo); }} sx={{ p: 0.25 }}>
                <WarningAmberIcon fontSize="small" sx={{ color: '#F5A623', fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ ...tdSx, pl: 1 }} title={formatCodigo(ativo.codigo1)}>{formatCodigo(ativo.codigo1)}</TableCell>
        <TableCell sx={tdSx} title={getNome(ativo)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
            <Box sx={{ ...ellipsis, flex: 1 }}>{getNome(ativo)}</Box>
            {hasDesvio && (
              <Tooltip title="Rent. diferente do esperado" placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 13, color: '#F5A623', flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell sx={tdSx}>{getClasse(ativo)}</TableCell>
        <TableCell sx={tdSx}>{getSubclasse(ativo)}</TableCell>
        <TableCell sx={{ ...tdSx, textAlign: 'right' }}>{formatQtd(ativo.quantidade)}</TableCell>
        <TableCell sx={{ ...tdSx, textAlign: 'right' }}>{formatBRL(ativo.valor_por_unidade)}</TableCell>
        <TableCell sx={{ ...tdSx, textAlign: 'right' }}>{formatBRL(ativo.valor_total)}</TableCell>
        <TableCell sx={{ ...tdSx, textAlign: 'right', color: rentColor(ativo.rent_cota_corrigida ?? ativo.ln_rent_ativo) }}>
          {ativo.rent_cota_corrigida != null
            ? formatRentCorrigida(ativo.rent_cota_corrigida)
            : formatRentDiaria(ativo.ln_rent_ativo)
          }
        </TableCell>
        <TableCell sx={{ ...tdSx, textAlign: 'right' }}>{formatPercent(ativo.concentracao)}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={NUM_COLS}
          sx={{
            py: 0,
            border: isExpanded ? '1px solid #CFD8E5' : 'none',
            borderTop: 'none',
          }}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ borderBottom: 1, borderColor: '#CFD8E5', backgroundColor: '#fff' }}>
              <AppTabs size="compact" value={activeTab} onChange={(_: React.SyntheticEvent, v: typeof activeTab) => setActiveTab(v)}>
                <Tab label="Movimentações" value="mov" />
                <Tab label="Rentabilidade" value="rent" />
                <Tab label="Histórico" value="hist" />
                {ativo.condominio === 'FECHADO' && ativo.ativo_master_id != null && (
                  <Tab label="Cronograma" value="cron" />
                )}
              </AppTabs>
            </Box>
            {activeTab === 'mov' && (
              <HistoricoMovimentacoesSection
                fundoId={fundoId}
                codigoAtivo={ativo.codigo1!}
                nomeAtivo={ativo.nome}
                adm={ativo.adm}
              />
            )}
            {activeTab === 'rent' && (
              <RentabilidadeAtivoSection
                fundoId={fundoId}
                codigo1={ativo.codigo1 ?? ''}
                adm={ativo.adm}
                nome={ativo.nome}
              />
            )}
            {activeTab === 'hist' && (
              <HistoricoAtivoSection
                fundoId={fundoId}
                codigo1={ativo.codigo1 ?? ''}
                adm={ativo.adm}
                nome={ativo.nome}
                classe={getClasse(ativo)}
                subclasse={ativo.subclasse}
              />
            )}
            {activeTab === 'cron' && ativo.ativo_master_id != null && (
              <CronogramaAtivoSection ativoMasterId={ativo.ativo_master_id} />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default AtivoTableRow;
